import { useCallback, useState } from 'react';
import { IUserRepository } from 'repository/U006/IUserRepository';
import { UserRepository } from 'repository/U006/UserRepository';
import { FetchError } from 'api/API';

type CreateUserRequest = {
  username: string;
  firstName: string;
  lastName: string;
  firstNameKana: string;
  lastNameKana: string;
  birthday: Date;
  mailAddress: string;
  parentName?: string;
  parentPhoneNumber?: string;
  auditionId?: string;
};
type CreateUserResponse = {
  status: string;
  msgid: string;
};

type CreateUserDoRequest = (param: {
  request: CreateUserRequest;
  onSuccess?: (response: CreateUserResponse) => void;
  onError?: (err: Error) => void;
  cancellation?: AbortSignal;
}) => Promise<void>;

export function useUpdateUserDetail(): [CreateUserDoRequest, boolean, Error | null] {
  const [loading, setLoading] = useState<boolean>(false);
  const [err, setError] = useState<Error | null>(null);

  const createUser = async (request: CreateUserRequest, cancellation: AbortSignal) => {
    const repository: IUserRepository = new UserRepository();

    return await repository.detailUpdate(
      request.username,
      request.firstName,
      request.lastName,
      request.firstNameKana,
      request.lastNameKana,
      request.birthday,
      request.mailAddress,
      cancellation,
      request.parentName ?? '',
      request.parentPhoneNumber ?? '',
      request.auditionId ?? ''
    );
  };

  const doRequest = useCallback<CreateUserDoRequest>(
    async ({ request, cancellation, onSuccess, onError }) => {
      const controller = new AbortController();
      const cancelSignal = cancellation ?? controller.signal;
      try {
        const user = await createUser(request, cancelSignal);
        onSuccess?.(user);
      } catch (err) {
        console.error({ err });
        if (err instanceof FetchError) {
          const errorData = await err.response.json();
          const flooredError = new Error(errorData.message || `HTTP ERROR: ${err.message}`);
          onError?.(flooredError);
          setError(flooredError);
        } else {
          const flooredError = new Error(`unknown error. ${err}`);
          onError?.(flooredError);
          setError(flooredError);
        }
      } finally {
        setLoading(false);
      }
    },
    [createUser, setLoading, setError]
  );

  return [doRequest, loading, err];
}
